/**
 * Recent deliveries for one webhook subscription, the Recent deliveries fold
 * of its Connections sheet: the latest hundred with a status filter. A row
 * expands inline with the payload preview and, on a `dead` row, Replay,
 * which POSTs to the route and re-reads the list. The subscription's own
 * verbs (regenerate the secret, pause, delete) are the sheet's.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  WebhookDelivery,
  WebhookDeliveryStatus,
  WebhookSubscriptionListItem,
} from "./types.js";

const STATUS_FILTERS: Array<{ value: WebhookDeliveryStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "in_flight", label: "In flight" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
  { value: "dead", label: "Dead" },
];

interface DeliveryInspectorProps {
  subscription: WebhookSubscriptionListItem;
  fetchImpl?: typeof fetch;
}

export function DeliveryInspector({ subscription, fetchImpl }: DeliveryInspectorProps) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    WebhookDeliveryStatus | "all"
  >("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [replayingId, setReplayingId] = useState<string | null>(null);

  const fetcher = fetchImpl ?? fetch;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher(
        `/api/webhook-subscriptions/${subscription.id}/deliveries?limit=100`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const body = (await res.json()) as { deliveries: WebhookDelivery[] };
      setDeliveries(body.deliveries);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [fetcher, subscription.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return deliveries;
    return deliveries.filter((d) => d.status === statusFilter);
  }, [deliveries, statusFilter]);

  function toggleExpanded(id: string) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function replay(deliveryId: string) {
    if (!window.confirm("Replay this delivery? Status will reset to pending.")) {
      return;
    }
    setReplayingId(deliveryId);
    setError(null);
    try {
      const res = await fetcher(
        `/api/webhook-subscriptions/${subscription.id}/deliveries/${deliveryId}/replay`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Replay failed (${res.status})`);
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Replay failed");
    } finally {
      setReplayingId(null);
    }
  }

  function timeToDeliver(d: WebhookDelivery): string {
    if (!d.delivered_at) return "·";
    const start = Date.parse(d.created_at);
    const end = Date.parse(d.delivered_at);
    if (Number.isNaN(start) || Number.isNaN(end)) return "·";
    const ms = Math.max(0, end - start);
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  return (
    <section
      className="flex flex-col gap-md"
      aria-label="Webhook subscription detail"
      data-testid={`webhook-inspector-${subscription.id}`}
    >
      <div className="flex items-center justify-between gap-md">
        <label className="flex items-center gap-xs text-sm">
          <span>Filter:</span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value as WebhookDeliveryStatus | "all",
              )
            }
            className="rounded-md border border-border bg-bg-input px-sm py-xs text-sm"
            data-testid="webhook-status-filter"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void reload()}
          className="rounded-md border border-border bg-[rgba(var(--glass-rgb),0.55)] hover:bg-[rgba(var(--glass-rgb),0.75)] px-md py-xs text-xs"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" className="text-sm text-error">
          {error}
        </div>
      )}
      {loading && <div className="text-sm text-text-secondary">Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div className="rounded-[var(--radius-card)] border-[1.5px] border-dashed border-[var(--dash-border)] p-md text-sm text-text-secondary">
          No deliveries to show.
        </div>
      )}

      {filtered.length > 0 && (
        <ul className="flex flex-col gap-xs">
          {filtered.map((d) => (
            <li
              key={d.id}
              className="rounded-[var(--radius-row)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)]"
              data-testid={`delivery-row-${d.id}`}
            >
              <button
                type="button"
                onClick={() => toggleExpanded(d.id)}
                className="flex w-full items-center justify-between gap-md p-sm text-left"
              >
                <div className="flex flex-col gap-xs">
                  <span className="font-mono text-xs">{d.event_kind}</span>
                  <span className="font-mono text-xs text-text-secondary">
                    {new Date(d.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-md font-mono text-xs">
                  <span className="rounded-full border border-border px-sm py-0.5">
                    {d.status}
                  </span>
                  <span>attempt {d.attempt_count}</span>
                  <span>
                    {d.last_response_status === null
                      ? "no response"
                      : `HTTP ${d.last_response_status}`}
                  </span>
                  <span>{timeToDeliver(d)}</span>
                </div>
              </button>
              {expanded[d.id] && (
                <div className="border-t border-border p-sm flex flex-col gap-sm">
                  {d.last_error && (
                    <div className="text-xs text-error">{d.last_error}</div>
                  )}
                  <pre className="text-xs whitespace-pre-wrap break-all bg-bg-input rounded-md p-sm font-mono">
                    {d.payload_preview}
                  </pre>
                  {d.status === "dead" && (
                    <div>
                      <button
                        type="button"
                        onClick={() => void replay(d.id)}
                        disabled={replayingId === d.id}
                        className="rounded-md border border-border bg-[rgba(var(--glass-rgb),0.55)] hover:bg-[rgba(var(--glass-rgb),0.75)] px-md py-xs text-xs"
                        data-testid={`delivery-replay-${d.id}`}
                      >
                        {replayingId === d.id ? "Replaying…" : "Replay"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

    </section>
  );
}
